import React, { useCallback, useMemo } from "react";
import { FieldInputProps, FormikErrors, FormikProps, withFormik } from "formik";
import { FormHelperText } from "@chakra-ui/form-control";
import { HStack, Text, VStack } from "@chakra-ui/layout";
import { Button } from "@chakra-ui/button";
import { ApiClient, ApiResult } from "../api/apiClient";
import { CherryPayApi } from "../api/models";
import { Checkbox, CheckboxGroup } from "@chakra-ui/checkbox";
import { Alert, AlertIcon } from "@chakra-ui/alert";
import { arraysAreEqual } from "../util/arraysAreEqual";
import { FieldWrapper } from "../components/fields/FieldWrapper/FieldWrapper";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";

interface EditUserFormValues {
  role: string[];
  authorisedVenues: string[];
}

interface EditUserFormProps {
  user: CherryPayApi.UserAccount;
  apiClient: ApiClient;
  businessId: string;
  business: CherryPayApi.BusinessSummary;
  rbacRoles: CherryPayApi.CherryHubRoleSummaryItem[];
  onCancel: () => void;
  onSuccess?: (user: CherryPayApi.UserAccount) => void;
  onFailure?: (message?: string) => void;
}

const getUserRoleIds = (userAccount: CherryPayApi.UserAccount): string[] => {
  const rbacRoleIds = userAccount.CustomClaims["CherryHubCherryPayRoles"];
  if (Array.isArray(rbacRoleIds)) {
    return rbacRoleIds;
  }
  return [];
};

const getUserAuthorisedVenues = (
  userAccount: CherryPayApi.UserAccount
): string[] => {
  const cherryPayAuthorisedVenues =
    userAccount.CustomClaims["cherryPayAuthorisedVenues"];
  if (typeof cherryPayAuthorisedVenues === "string") {
    return cherryPayAuthorisedVenues.split(",") as string[];
  }
  return [];
};

const AuthorisedVenueCheckboxGroup = ({
  field,
  form,
  venues,
}: {
  field: FieldInputProps<number[]>;
  form: FormikProps<any>;
  venues: CherryPayApi.VenueSummary[];
}) => {
  const onChange = useCallback(
    (val: string[]) => form.setFieldValue(field.name, val),
    [form.setFieldValue]
  );

  return (
    <CheckboxGroup {...field} onChange={onChange}>
      <VStack w="100%" mt="2" mb="2">
        {venues.map(({ DisplayName, VenueNo }) => {
          return (
            <Checkbox key={VenueNo} value={VenueNo.toString()} w="100%">
              {DisplayName}
            </Checkbox>
          );
        })}
      </VStack>
    </CheckboxGroup>
  );
};

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  onCancel,
  values,
  business,
  rbacRoles,
  dirty,
}: EditUserFormProps & FormikProps<EditUserFormValues>) => {
  const roleOptions = useMemo(() => {
    // Separate system and non-system roles
    const systemRoles = rbacRoles.filter((role) => role.IsSystemRole);
    const nonSystemRoles = rbacRoles.filter((role) => !role.IsSystemRole);

    // Display system roles first, then non-system roles
    return [...systemRoles, ...nonSystemRoles].map((role) => ({
      label: role.DisplayName ?? "",
      value: role.RoleId ?? "",
    }));
  }, [rbacRoles]);

  return (
    <FormStack>
      <ChakraReactSelectField
        label="Role(s)"
        name="role"
        isMulti
        keepSelectionOrderInOptions={true}
        options={roleOptions}
      />

      <FieldWrapper name="authorisedVenues" label="Restrict Venue Access">
        {({ field, form }) => (
          <>
            <FormHelperText>
              If this users access should be restricted to specific venues,
              select them below.
            </FormHelperText>
            <AuthorisedVenueCheckboxGroup
              venues={business.VenueSummaries}
              form={form}
              field={field}
            />
            {values.authorisedVenues.length === 0 && (
              <Alert status="info" size="sm">
                <AlertIcon />
                <Text>
                  This user will have access to <strong>all</strong> venues
                  within the business.
                </Text>
              </Alert>
            )}
          </>
        )}
      </FieldWrapper>

      <HStack width="100%" justifyContent="end" spacing="3" pt="8">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={dirty ? submitForm : onCancel}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const EditUserForm = withFormik<EditUserFormProps, EditUserFormValues>({
  mapPropsToValues: (props) => {
    return {
      role: getUserRoleIds(props.user),
      authorisedVenues: getUserAuthorisedVenues(props.user),
    };
  },

  handleSubmit: async (values, { props }) => {
    let result: ApiResult<CherryPayApi.UserAccount> | null = null;

    const currentRoleIds = getUserRoleIds(props.user);
    const rolesChanged = !arraysAreEqual(values.role, currentRoleIds);

    // If the user role has been edited
    if (values.role && rolesChanged) {
      result = await props.apiClient.updateUserRoles(
        props.businessId,
        props.user.Uid,
        values.role
      );

      if (!result.ok) {
        if (props.onFailure) {
          props.onFailure(result.message);
        }
        return;
      }
    }

    if (
      !arraysAreEqual(
        values.authorisedVenues,
        getUserAuthorisedVenues(props.user)
      )
    ) {
      result = await props.apiClient.updateUserAuthorisedVenues(
        props.businessId,
        props.user.Uid,
        values.authorisedVenues
      );

      if (!result.ok) {
        if (props.onFailure) {
          props.onFailure(result.message);
        }
        return;
      }
    }

    if (props.onSuccess && result?.data) {
      props.onSuccess(result?.data);
    }
  },

  validateOnBlur: false,

  validate: (values) => {
    let errors: FormikErrors<EditUserFormValues> = {};

    if (!Array.isArray(values.role) || values.role.length === 0) {
      errors.role = "At least one role must be selected.";
    }

    return errors;
  },
})(InnerForm);
